import {
  asTeamOrgId,
  type TeamJwt,
  type TeamMemberId,
  type TeamOrgId,
} from '@nimbalyst/collab-bundle/editor';
import { isEmbedded } from '../embedded/embedMarker';
import {
  hydrateNativeSession,
  NativeSessionError,
  requestNativeSession,
  type NativeSessionTokens,
} from '../embedded/nativeSession';

/** Browser equivalent of the compiler-enforced two-JWT model. */
declare const browserSessionJwtBrand: unique symbol;
declare const personalJwtBrand: unique symbol;
declare const personalMemberIdBrand: unique symbol;

/** A Stytch browser session JWT before its organization scope is selected. */
export type BrowserSessionJwt = string & { readonly [browserSessionJwtBrand]: true };
/** Reserved for personal/mobile sync. Never accepted by team collaboration APIs. */
export type PersonalJwt = string & { readonly [personalJwtBrand]: true };
/** A member id in a personal organization. */
export type PersonalMemberId = string & { readonly [personalMemberIdBrand]: true };
export type { TeamJwt, TeamMemberId, TeamOrgId };

export interface TeamSession {
  jwt: TeamJwt;
  memberId: TeamMemberId;
  orgId: TeamOrgId;
  expiresAt: number;
}

export interface BrowserSessionClient {
  getTokens(): { session_jwt: string } | null;
  authenticate(options: { session_duration_minutes: number }): Promise<{ session_jwt?: string | null }>;
  exchange(options: {
    organization_id: string;
    session_duration_minutes: number;
  }): Promise<{ session_jwt?: string | null }>;
  /** Adopt tokens minted elsewhere (the phone app). Required in embedded mode. */
  updateSession?(tokens: { session_token: string; session_jwt: string }): void;
}

/**
 * Where an embedded console gets a session from: the phone app, through the
 * bridge. Present only inside the app. Minted sessions cannot
 * `session.exchange`, so with a port present this module never calls it: a
 * different org or an expired session is a new request to the app.
 */
export interface NativeSessionPort {
  request(orgId: string | null, reason: 'switch-org' | 'expired'): Promise<NativeSessionTokens>;
}

function defaultNativeSessionPort(): NativeSessionPort | null {
  return isEmbedded() ? { request: requestNativeSession } : null;
}

export type TeamSessionErrorKind =
  | 'session-expired'
  | 'service-unavailable'
  | 'organization-unavailable'
  | 'wrong-org'
  | 'member-mismatch'
  | 'malformed-token'
  | 'org-auth-required';

export class TeamSessionError extends Error {
  readonly kind: TeamSessionErrorKind;

  constructor(
    kind: TeamSessionErrorKind,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'TeamSessionError';
    this.kind = kind;
  }
}

interface StytchJwtPayload {
  sub?: unknown;
  exp?: unknown;
  'https://stytch.com/organization'?: {
    organization_id?: unknown;
  };
}

const REFRESH_WINDOW_SECONDS = 30;

function decodeBase64Url(value: string): string {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
  try {
    return globalThis.atob(normalized + padding);
  } catch (cause) {
    throw new TeamSessionError('malformed-token', 'The browser session token could not be decoded.', { cause });
  }
}

function readPayload(jwt: string): StytchJwtPayload {
  // This client-side decode only selects and brands scope. The CollabV3 worker
  // remains the authorization authority and verifies signature, issuer, and audience.
  const payloadPart = jwt.split('.')[1];
  if (!payloadPart) {
    throw new TeamSessionError('malformed-token', 'The browser session token is malformed.');
  }
  try {
    return JSON.parse(decodeBase64Url(payloadPart)) as StytchJwtPayload;
  } catch (cause) {
    if (cause instanceof TeamSessionError) throw cause;
    throw new TeamSessionError('malformed-token', 'The browser session token has an invalid payload.', { cause });
  }
}

function requireUnexpiredPayload(jwt: string, nowSeconds: number): StytchJwtPayload {
  const payload = readPayload(jwt);
  if (typeof payload.exp !== 'number' || payload.exp <= nowSeconds + REFRESH_WINDOW_SECONDS) {
    throw new TeamSessionError('session-expired', 'Your browser session has expired. Please sign in again.');
  }
  return payload;
}

function asBrowserSessionJwt(jwt: string): BrowserSessionJwt {
  return jwt as BrowserSessionJwt;
}

function asTeamJwt(jwt: string): TeamJwt {
  return jwt as TeamJwt;
}

/**
 * The one place this app brands an org-scoped member id. Exported so callers
 * that legitimately hold a raw string — a test fixture, or the empty "viewer
 * not yet resolved" sentinel — go through a named cast rather than sprinkling
 * `as TeamMemberId` and defeating the point of the brand.
 */
export function asTeamMemberId(memberId: string): TeamMemberId {
  return memberId as TeamMemberId;
}

function sessionJwtFrom(result: { session_jwt?: string | null }): string | null {
  return typeof result.session_jwt === 'string' && result.session_jwt.length > 0
    ? result.session_jwt
    : null;
}

function stytchFailureKind(cause: unknown): TeamSessionErrorKind {
  const record = cause && typeof cause === 'object' ? cause as Record<string, unknown> : null;
  const code = [record?.error_type, record?.errorType, record?.message]
    .find(value => typeof value === 'string') as string | undefined;
  const normalized = code?.toLowerCase() || '';
  if (normalized.includes('session') || normalized.includes('unauthenticated') || normalized.includes('token')) {
    return 'session-expired';
  }
  if (normalized.includes('member') || normalized.includes('organization') || normalized.includes('unauthorized')) {
    return 'organization-unavailable';
  }
  return 'service-unavailable';
}

function nativeFailureKind(cause: unknown): TeamSessionErrorKind {
  if (!(cause instanceof NativeSessionError)) return 'service-unavailable';
  if (cause.code === 'org_auth_required') return 'org-auth-required';
  if (cause.code === 'not_a_member') return 'organization-unavailable';
  return 'service-unavailable';
}

/** Ask the app for a session, adopt it in the SDK, and return the JWT it now holds. */
async function adoptNativeSession(
  session: BrowserSessionClient,
  native: NativeSessionPort,
  orgId: string | null,
  reason: 'switch-org' | 'expired',
  sessionDurationMinutes: number,
  nowSeconds = Date.now() / 1000,
): Promise<BrowserSessionJwt> {
  const { updateSession } = session;
  if (!updateSession) {
    throw new TeamSessionError('service-unavailable', 'This browser session cannot adopt a session from the app.');
  }
  let jwt: string;
  try {
    const tokens = await native.request(orgId, reason);
    jwt = await hydrateNativeSession({
      updateSession: (tokens) => updateSession.call(session, tokens),
      authenticate: (options) => session.authenticate(options),
    }, tokens, sessionDurationMinutes);
  } catch (cause) {
    const kind = nativeFailureKind(cause);
    throw new TeamSessionError(kind, cause instanceof Error ? cause.message : 'The app could not sign this page in.', { cause });
  }
  requireUnexpiredPayload(jwt, nowSeconds);
  return asBrowserSessionJwt(jwt);
}

/**
 * Return a fresh browser JWT for bootstrap calls such as team discovery.
 * Stytch normally refreshes its five-minute JWT in the background; the explicit
 * authenticate fallback covers a stale token without pretending a dead session
 * is an empty team list.
 */
export async function getFreshBrowserSessionJwt(
  session: BrowserSessionClient,
  sessionDurationMinutes: number,
  nowSeconds = Date.now() / 1000,
  options?: { native?: NativeSessionPort | null; orgId?: string | null },
): Promise<BrowserSessionJwt> {
  const native = options?.native === undefined ? defaultNativeSessionPort() : options.native;
  if (native) {
    try {
      return await getFreshBrowserSessionJwt(session, sessionDurationMinutes, nowSeconds, { native: null });
    } catch (cause) {
      if (!(cause instanceof TeamSessionError) || cause.kind !== 'session-expired') throw cause;
      return adoptNativeSession(session, native, options?.orgId ?? null, 'expired', sessionDurationMinutes, nowSeconds);
    }
  }

  const currentJwt = session.getTokens()?.session_jwt;
  if (currentJwt) {
    try {
      requireUnexpiredPayload(currentJwt, nowSeconds);
      return asBrowserSessionJwt(currentJwt);
    } catch (cause) {
      if (!(cause instanceof TeamSessionError) || cause.kind !== 'session-expired') throw cause;
    }
  }

  try {
    const refreshedJwt = sessionJwtFrom(await session.authenticate({
      session_duration_minutes: sessionDurationMinutes,
    }));
    if (!refreshedJwt) {
      throw new TeamSessionError('session-expired', 'Your browser session has expired. Please sign in again.');
    }
    requireUnexpiredPayload(refreshedJwt, nowSeconds);
    return asBrowserSessionJwt(refreshedJwt);
  } catch (cause) {
    if (cause instanceof TeamSessionError && cause.kind !== 'session-expired') throw cause;
    const kind = stytchFailureKind(cause);
    const message = kind === 'session-expired'
      ? 'Your browser session has expired. Please sign in again.'
      : 'The browser session service is unavailable. Your route has been preserved.';
    throw new TeamSessionError(kind, message, { cause });
  }
}

/** Prove that a raw browser JWT belongs to the requested team organization. */
export function readTeamSession(
  jwt: BrowserSessionJwt,
  requestedOrgId: string,
  expectedMemberId?: string | null,
  nowSeconds = Date.now() / 1000,
): TeamSession {
  const payload = requireUnexpiredPayload(jwt, nowSeconds);
  const tokenOrgId = payload['https://stytch.com/organization']?.organization_id;
  if (typeof tokenOrgId !== 'string' || tokenOrgId !== requestedOrgId) {
    throw new TeamSessionError('wrong-org', 'The browser session is scoped to a different organization.');
  }
  if (typeof payload.sub !== 'string' || payload.sub.length === 0) {
    throw new TeamSessionError('malformed-token', 'The browser session token is missing its organization member id.');
  }
  if (expectedMemberId && payload.sub !== expectedMemberId) {
    throw new TeamSessionError('member-mismatch', 'The browser session member does not match this organization membership.');
  }
  return {
    jwt: asTeamJwt(jwt),
    memberId: asTeamMemberId(payload.sub),
    orgId: asTeamOrgId(tokenOrgId),
    expiresAt: payload.exp as number,
  };
}

const inFlightTeamSessions = new WeakMap<object, Map<string, Promise<TeamSession>>>();

/**
 * Select the requested Stytch B2B organization, then return only a team-branded
 * JWT and member id whose claims match that organization.
 */
export function getTeamSessionForOrg(
  session: BrowserSessionClient,
  requestedOrgId: string,
  sessionDurationMinutes: number,
  expectedMemberId?: string | null,
  options?: { forceRefresh?: boolean; native?: NativeSessionPort | null },
): Promise<TeamSession> {
  const native = options?.native === undefined ? defaultNativeSessionPort() : options.native;
  let requests = inFlightTeamSessions.get(session);
  if (!requests) {
    requests = new Map();
    inFlightTeamSessions.set(session, requests);
  }
  const requestKey = `${requestedOrgId}:${expectedMemberId || ''}:${options?.forceRefresh === true}`;
  const existing = requests.get(requestKey);
  if (existing) return existing;

  const request = (async () => {
    if (native) {
      return getEmbeddedTeamSession(session, native, requestedOrgId, sessionDurationMinutes, expectedMemberId, options?.forceRefresh === true);
    }
    if (options?.forceRefresh) {
      let exchangedJwt: string | null;
      try {
        exchangedJwt = sessionJwtFrom(await session.exchange({
          organization_id: requestedOrgId,
          session_duration_minutes: sessionDurationMinutes,
        }));
      } catch (cause) {
        const kind = stytchFailureKind(cause);
        const message = kind === 'session-expired'
          ? 'Your organization session could not be refreshed. Please sign in again.'
          : kind === 'organization-unavailable'
            ? 'Your organization membership is no longer available.'
            : 'The organization session service is unavailable. Your route has been preserved.';
        throw new TeamSessionError(kind, message, { cause });
      }
      if (!exchangedJwt) {
        throw new TeamSessionError('session-expired', 'Your organization session could not be refreshed. Please sign in again.');
      }
      return readTeamSession(asBrowserSessionJwt(exchangedJwt), requestedOrgId, expectedMemberId);
    }

    const browserJwt = await getFreshBrowserSessionJwt(session, sessionDurationMinutes);
    try {
      return readTeamSession(browserJwt, requestedOrgId, expectedMemberId);
    } catch (cause) {
      if (!(cause instanceof TeamSessionError) || cause.kind !== 'wrong-org') throw cause;
    }

    let exchangedJwt: string | null;
    try {
      exchangedJwt = sessionJwtFrom(await session.exchange({
        organization_id: requestedOrgId,
        session_duration_minutes: sessionDurationMinutes,
      }));
    } catch (cause) {
      const kind = stytchFailureKind(cause);
      const message = kind === 'session-expired'
        ? 'Your organization session could not be refreshed. Please sign in again.'
        : kind === 'organization-unavailable'
          ? 'Your organization membership is no longer available.'
          : 'The organization session service is unavailable. Your route has been preserved.';
      throw new TeamSessionError(kind, message, { cause });
    }
    if (!exchangedJwt) {
      throw new TeamSessionError('session-expired', 'Your organization session could not be refreshed. Please sign in again.');
    }
    return readTeamSession(asBrowserSessionJwt(exchangedJwt), requestedOrgId, expectedMemberId);
  })();

  requests.set(requestKey, request);
  void request.then(
    () => requests?.delete(requestKey),
    () => requests?.delete(requestKey),
  );
  return request;
}

/**
 * The embedded path: never `session.exchange`. A forced refresh re-reads the
 * current session with `authenticate()`; a session for another org asks the
 * app for one in this org; an expired session asks the app to re-mint.
 */
async function getEmbeddedTeamSession(
  session: BrowserSessionClient,
  native: NativeSessionPort,
  requestedOrgId: string,
  sessionDurationMinutes: number,
  expectedMemberId: string | null | undefined,
  forceRefresh: boolean,
): Promise<TeamSession> {
  let browserJwt: BrowserSessionJwt | null = null;
  try {
    if (forceRefresh) {
      const refreshed = sessionJwtFrom(await session.authenticate({ session_duration_minutes: sessionDurationMinutes }));
      if (refreshed) {
        requireUnexpiredPayload(refreshed, Date.now() / 1000);
        browserJwt = asBrowserSessionJwt(refreshed);
      }
    } else {
      browserJwt = await getFreshBrowserSessionJwt(session, sessionDurationMinutes, undefined, { native: null });
    }
  } catch (cause) {
    const kind = cause instanceof TeamSessionError ? cause.kind : stytchFailureKind(cause);
    if (kind !== 'session-expired') {
      throw cause instanceof TeamSessionError
        ? cause
        : new TeamSessionError(kind, 'The organization session service is unavailable. Your route has been preserved.', { cause });
    }
    browserJwt = null;
  }
  if (!browserJwt) {
    const adopted = await adoptNativeSession(session, native, requestedOrgId, 'expired', sessionDurationMinutes);
    return readTeamSession(adopted, requestedOrgId, expectedMemberId);
  }
  try {
    return readTeamSession(browserJwt, requestedOrgId, expectedMemberId);
  } catch (cause) {
    if (!(cause instanceof TeamSessionError) || cause.kind !== 'wrong-org') throw cause;
  }
  const adopted = await adoptNativeSession(session, native, requestedOrgId, 'switch-org', sessionDurationMinutes);
  return readTeamSession(adopted, requestedOrgId, expectedMemberId);
}
